/**
 * @fileoverview Job Phase Select
 *
 * Purpose: Inline, badge-styled selector for a job's phase (its `status`).
 * Lets owners/members change the phase directly from the job header without
 * navigating to Settings. Viewers see a read-only badge.
 *
 * "Phase" is a user-facing relabelling of the job `status` field — the values
 * (`day_one`, `active`, ...) and the `updateJob` mutation are unchanged.
 *
 * Dependencies:
 * - Convex: jobs.mutations.updateJob
 * - shadcn Select + Badge (badgeVariants for the trigger styling)
 *
 * Used by:
 * - Dashboard layout header (_layout.tsx)
 *
 * @example
 * <JobPhaseSelect jobId={job._id} status={job.status} userRole={job.userRole} />
 */

import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { ChevronDown } from "lucide-react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { JobRole, JobStatus } from "@cvx/jobs/schema";
import { Badge } from "@/ui/primitives/badge";
import { badgeVariants } from "@/ui/primitives/badge.variants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/ui/primitives/select";
import {
  JOB_STATUS_LABELS,
  JOB_STATUS_OPTIONS,
  JOB_STATUS_VARIANTS,
} from "@/lib/job-statuses";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

/**
 * Filter the selectable phases for a job. Mirrors the rule used in Job
 * Settings: pre-appointment jobs can only stay pre-appointment (they convert
 * via the Overview page), and `did_not_proceed` only shows when already set.
 */
function selectablePhases(current: JobStatus) {
  return JOB_STATUS_OPTIONS.filter(({ value }) => {
    if (value === "did_not_proceed") return current === "did_not_proceed";
    if (current === "pre_appointment") return value === "pre_appointment";
    return value !== "pre_appointment";
  });
}

interface JobPhaseSelectProps {
  jobId: Id<"jobs">;
  status: JobStatus;
  userRole: JobRole;
}

/**
 * Editable phase badge. Renders a read-only badge for viewers, and a compact
 * badge-styled dropdown for owners/members that commits changes immediately.
 */
export function JobPhaseSelect({
  jobId,
  status,
  userRole,
}: JobPhaseSelectProps) {
  const canEdit = userRole === "owner" || userRole === "member";

  const { mutateAsync: updateJob, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });

  const variant = JOB_STATUS_VARIANTS[status] ?? "outline";
  const label = JOB_STATUS_LABELS[status] ?? status;

  // Viewers, pre-appointment jobs (no valid alternative), or an in-flight
  // update all render the plain badge — there's nothing to switch to.
  const options = selectablePhases(status);
  if (!canEdit || options.length <= 1) {
    return (
      <Badge variant={variant} size="sm">
        {label}
      </Badge>
    );
  }

  const handleChange = async (next: string) => {
    if (next === status) return;
    try {
      await updateJob({ jobId, status: next as JobStatus });
      toast.success(`Phase updated to ${JOB_STATUS_LABELS[next] ?? next}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update phase"));
    }
  };

  return (
    <Select value={status} onValueChange={handleChange} disabled={isPending}>
      <SelectPrimitive.Trigger
        aria-label="Job phase"
        className={cn(
          badgeVariants({ variant, size: "sm" }),
          "cursor-pointer focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
        )}
      >
        <SelectValue />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-3 opacity-70" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectContent>
        {options.map(({ value, label: optLabel }) => (
          <SelectItem key={value} value={value}>
            {optLabel}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
